import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/** @type {boolean} */
let cartDrawerUpsellsListenerAttached = false;

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Aborts the cart drawer upsells dialog:open listener on disconnect
   * @type {AbortController | null}
   */
  #dialogOpenController = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.classList.contains('cart-drawer-upsells')) {
      this.#dialogOpenController = new AbortController();
      const cartDrawer = this.closest('cart-drawer-component');
      cartDrawer?.addEventListener('dialog:open', this.#handleCartDrawerOpen, {
        signal: this.#dialogOpenController.signal,
      });

      ProductRecommendations.#ensureCartDrawerUpsellsListener();
      this.#loadCartDrawerUpsells();
    } else {
      this.#intersectionObserver.observe(this);
    }

    if (!this.classList.contains('cart-drawer-upsells')) {
      this.#mutationObserver.observe(this, { attributes: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#dialogOpenController?.abort();
    this.#dialogOpenController = null;
  }

  static #ensureCartDrawerUpsellsListener() {
    if (cartDrawerUpsellsListenerAttached) return;

    document.addEventListener(ThemeEvents.cartUpdate, ProductRecommendations.#handleGlobalCartDrawerUpdate);
    cartDrawerUpsellsListenerAttached = true;
  }

  /**
   * @param {CustomEvent} event
   */
  static #handleGlobalCartDrawerUpdate = async (event) => {
    const upsellsElement = ProductRecommendations.#getCartDrawerUpsellsElement();
    if (!(upsellsElement instanceof ProductRecommendations)) return;

    let cart = /** @type {Cart | undefined} */ (event.detail?.resource);

    if (!cart?.items) {
      try {
        const response = await fetch('/cart.js');
        cart = await response.json();
      } catch {
        return;
      }
    }

    upsellsElement.#reloadCartDrawerRecommendations(cart);
  };

  static #getCartDrawerUpsellsElement() {
    return document.querySelector('cart-drawer-component product-recommendations.cart-drawer-upsells');
  }

  #handleCartDrawerOpen = () => {
    this.dataset.recommendationsPerformed = 'false';
    this.#loadCartDrawerUpsells();
  };

  /**
   * @param {Cart} cart
   */
  #reloadCartDrawerRecommendations(cart) {
    if (!cart.items?.length) {
      this.classList.add('hidden');
      return;
    }

    this.#cachedRecommendations = {};
    this.#activeFetch?.abort();

    this.classList.remove('hidden');
    this.removeAttribute('data-error');
    this.dataset.sectionId = 'cart-drawer-recommendations';
    this.dataset.recommendationsPerformed = 'false';

    this.#loadCartDrawerUpsells();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    if (this.classList.contains('cart-drawer-upsells')) {
      this.#loadCartDrawerUpsells();
      return;
    }

    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          const headerSectionId = this.dataset.headerSectionId;
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true }).then(() => {
            if (!headerSectionId) return;

            this.dataset.headerSectionId = headerSectionId;
            this.querySelectorAll('product-form-component').forEach((form) => {
              if (form instanceof HTMLElement) {
                form.dataset.sectionId = headerSectionId;
              }
            });
          });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Load conditional cart drawer upsells from the cart section renderer.
   */
  #loadCartDrawerUpsells() {
    const { recommendationsPerformed, sectionId } = this.dataset;

    if (!sectionId) {
      throw new Error('A section ID is required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    const url = `/cart?section_id=${sectionId}&_=${Date.now()}`;

    this.#fetchCartDrawerUpsells(url)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (!result.data?.trim().length) {
          this.classList.add('hidden');
          this.dataset.recommendationsPerformed = 'true';
          return;
        }

        const headerSectionId = this.dataset.headerSectionId;
        this.dataset.recommendationsPerformed = 'true';
        morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true }).then(() => {
          const upsellsElement = ProductRecommendations.#getCartDrawerUpsellsElement();

          if (!(upsellsElement instanceof HTMLElement)) return;

          if (upsellsElement.classList.contains('hidden')) {
            upsellsElement.classList.add('hidden');
          } else {
            upsellsElement.classList.remove('hidden');
          }

          if (!headerSectionId) return;

          upsellsElement.dataset.headerSectionId = headerSectionId;
          upsellsElement.querySelectorAll('product-form-component').forEach((form) => {
            if (form instanceof HTMLElement) {
              form.dataset.sectionId = headerSectionId;
            }
          });
        });
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  /**
   * Fetches cart drawer upsells HTML.
   * @param {string} url
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCartDrawerUpsells(url) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error(error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
